import {GameStateEnums} from '../../../../../shared/GameEnums'
import {
    CardPopupInformation,
    CardDataController, SelectCardPopupUsingDevelopmentCardFormat, SelectCardPopupForPendingDistributionFormat,
    SelectCardPopupStealCardFromAnotherPlayerFormat, SelectCardToDiscardFormat, SelectCardsToGiveToAnotherPlayer,
    SelectPlayerUsingDevelopmentCardFormat, SelectPlayerAndResourceCardFormat,
} from '@colonist/game-card-types'
import {SelectDiceFormat, PopupInformation, SelectPlayerRobberFormat, ModeSettingEnum} from '@colonist/game-base-types'
import {UIGameManager} from '../UIGameManager'
import {UIGameMechanic} from '../UIGameEventController'
import {Sound} from '../../Sound'
import {UIGamePopupSelectCards} from './UIGamePopupSelectCards'
import {UIGamePopupInformationView} from './UIGamePopupInformationView'
import {UIGamePopupSelectDevelopmentCardFromPlayer} from './UIGamePopupSelectDevelopmentCardFromPlayer'
import {UIGamePopupSelectResourcesCardsFromPlayer} from './UIGamePopupSelectResourcesCardsFromPlayer'
import {UIGamePopupSelectPlayers} from './UIGamePopupSelectPlayers'
import {UIGamePopupSelectDice} from './UIGamePopupSelectDice'
import {UIGamePopupSelectPlayerAndResourceCard} from './UIGamePopupSelectPlayerAndResourceCard'
import {UIGamePopupCardConfirmation} from './UIGamePopupCardConfirmation'
import {UIGamePopupDiscardCards} from './UIGamePopupDiscardCards'
import {UIGamePopupSelectCardToGiveToAnotherPlayer} from './UIGamePopupSelectCardToGiveToAnotherPlayer'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameComponentZIndex} from '../UIGameComponentZIndex'
import {UIGamePopupMechanicIntroductionForBeginners} from './UIGamePopupMechanicIntroductionForBeginners'
import {ClientUserState} from '../../ClientUserState'
import {ActionState, MechanicProgressCardsState, TurnState} from '@colonist/game-states'
import {robberSkinController} from '../UIGameRobberSkinController'
import {Sprites} from '../UIGameImageDictionary'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGamePopupController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected readonly progressCardsState: MechanicProgressCardsState | undefined
    private currentView: SectionBgAdaptiveView | undefined
    private turnStateOnPopupInitialization: TurnState
    private previousTurnState: TurnState
    private allowableActionState: ActionState
    private shouldShowBeginnerPopups: boolean
    protected readonly popupW: number
    onCanvasResize?: (() => void) | undefined

    constructor(uiGameManager: UIGameManager, progressCardsState: MechanicProgressCardsState | undefined) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.progressCardsState = progressCardsState
        this.currentView = undefined
        this.onCanvasResize = undefined
        this.turnStateOnPopupInitialization = TurnState.InitialPlacement
        this.previousTurnState = TurnState.InitialPlacement
        this.allowableActionState = ActionState.None
        const canvas = this.uiGameManager.canvasController
        this.popupW = canvas.popupWidth
        this.shouldShowBeginnerPopups = this.getShouldShowBeginnerPopups()
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.ConfirmUseDevelopmentCard: this.showCardConfirmationPopup(data); break
            case UIGameEventEnums.SelectCardsFromBankUsingDevelopmentCard: this.showCardSelectorWindowForDevelopmentCard(data); break
            case UIGameEventEnums.SelectCardsForPendingDistribution: this.showCardSelectorWindowForPendingDistributions(data); break
            case UIGameEventEnums.SelectCardToGiveToAnotherPlayer: this.showCardSelectorWindowForGivingCardToAnotherPlayer(data); break
            case UIGameEventEnums.SelectCardsToDiscard: this.showCardDiscardPopup(data); break
            case UIGameEventEnums.SelectPlayer: this.showSelectPlayer(data); break
            case UIGameEventEnums.StealDevelopmentCardFromPlayer: this.showCardSelectorWindowToStealDevelopmentCard(data); break
            case UIGameEventEnums.StealResourceCardFromPlayer: this.showCardSelectorWindowToStealResourceCard(data); break
            case UIGameEventEnums.SelectPlayerForDevelopmentCard: this.showSelectPlayerUsingDevelopmentCard(data); break
            case UIGameEventEnums.SelectPlayerAndCard: this.showSelectPlayerAndResourceCard(data); break
            case UIGameEventEnums.SelectDice: this.showSelectDice(data); break
            case UIGameEventEnums.GameStateUpdated: this.onGameStateUpdated(); break
            case UIGameEventEnums.ClosePopupUI: this.closeUI(); break
            case UIGameEventEnums.ShowTradeUI: this.closeUIOnShowTrade(); break
            case UIGameEventEnums.CanvasResized: if(this.onCanvasResize != undefined) this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeStart: this.shouldShowBeginnerPopups = true; break
            default: break
        }
    }

    protected abstract getViewForCardConfirmationPopup(cardPopupInformationFormat: CardPopupInformation): UIGamePopupCardConfirmation
    protected abstract getViewForMechanicIntroductionForBeginners(popupImageAndDescription: PopupInformation): UIGamePopupMechanicIntroductionForBeginners
    protected abstract getPopupImageAndDescriptionForCardSelectorWindowForDevelopmentCard(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForCardSelectorWindowForDevelopmentCard(developmentCardSelectFormat: SelectCardPopupUsingDevelopmentCardFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectCards
    protected abstract getPopupImageAndDescriptionForCardSelectorWindowForPendingDistributions(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForCardSelectorWindowForPendingDistributions(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectCards
    protected abstract getPopupImageAndDescriptionForCardSelectorWindowForGivingCardToAnotherPlayer(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForCardSelectorWindowForGivingCardToAnotherPlayer(selectCardFormat: SelectCardsToGiveToAnotherPlayer, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectCardToGiveToAnotherPlayer
    protected abstract getViewForCardDiscardPopup(discardCardFormat: SelectCardToDiscardFormat): UIGamePopupDiscardCards
    protected abstract getPopupImageAndDescriptionForCardSelectorWindowToStealDevelopmentCard(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForCardSelectorWindowToStealDevelopmentCard(stealDevelopmentCardFormat: SelectCardPopupStealCardFromAnotherPlayerFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectDevelopmentCardFromPlayer
    protected abstract getPopupImageAndDescriptionForCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer: SelectCardPopupStealCardFromAnotherPlayerFormat, popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer: SelectCardPopupStealCardFromAnotherPlayerFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectResourcesCardsFromPlayer
    protected abstract getPopupImageAndDescriptionForSelectPlayerUsingDevelopmentCard(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getPopupImageAndDescriptionForSelectPlayerAndResourceCard(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForSelectPlayerAndResourceCard(selectionFormat: SelectPlayerAndResourceCardFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectPlayerAndResourceCard
    protected abstract getPopupImageAndDescriptionForSelectDice(popupInformation: PopupInformation): UIGamePopupInformationView
    protected abstract getViewForSelectDice(selectDiceFormat: SelectDiceFormat, popupImageAndDescription: UIGamePopupInformationView): UIGamePopupSelectDice

    private getPopupImageAndDescriptionForSelectPlayerToRob(isPirate: boolean): UIGamePopupInformationView {
        const popupInformation: PopupInformation = {
            imageTextureName: isPirate ? Sprites.icon_pirate_ship : robberSkinController.getRobberSkin(),
            title: {key: isPirate ? 'strings:game.tips.pirate.title' : 'strings:game.tips.robber.title'},
            body: {key: 'strings:game.prompts.selectWhoToRobFrom'},
        }

        const canvas = this.uiGameManager.canvasController
        const w = canvas.popupWidth
        const informationView = new UIGamePopupInformationView(this.uiGameManager, 0, 0, w, popupInformation)
        informationView.scaleImage(0.8)
        return informationView
    }

    private setNewView(newView: SectionBgAdaptiveView, newAllowableActionState: ActionState) {
        this.closeUI()
        this.currentView = newView
        this.currentView.zIndex = UIGameComponentZIndex.Popup
        this.allowableActionState = newAllowableActionState
        this.turnStateOnPopupInitialization = this.uiGameManager.gameController.currentState.turnState
        newView.interactive = true
        this.uiGameManager.eventController.sendEvent(newView, UIGameEventEnums.PopupActive)
    }

    private showCardConfirmationPopup(cardPopupInformationFormat: CardPopupInformation) {
        const newView = this.getViewForCardConfirmationPopup(cardPopupInformationFormat)
        const defaultAllowableActionState = ActionState.None
        this.setNewView(newView, cardPopupInformationFormat.allowableActionState ?? defaultAllowableActionState)
    }

    protected abstract createPopupCardConfirmationView(cardPopupInformationFormat: CardPopupInformation): UIGamePopupCardConfirmation

    private showCardSelectorWindowForDevelopmentCard(developmentCardSelectFormat: SelectCardPopupUsingDevelopmentCardFormat) {
        this.playSoundForCardSelectorWindow()

        const cardData = CardDataController.getCardDataForCard(developmentCardSelectFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForCardSelectorWindowForDevelopmentCard(popupInformation)

        const newView = this.getViewForCardSelectorWindowForDevelopmentCard(developmentCardSelectFormat, popupImageAndDescription)
        this.setNewView(newView, developmentCardSelectFormat.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardSelectorWindowForDevelopmentCard(developmentCardSelectFormat)
    }

    private showCardSelectorWindowForPendingDistributions(pendingDistributionSelectFormat: SelectCardPopupForPendingDistributionFormat) {
        const popupInformation = UIGamePopupInformationView.getPopupInformationForPendingDistributions(pendingDistributionSelectFormat)
        if(popupInformation == undefined) return

        const popupImageAndDescription = this.getPopupImageAndDescriptionForCardSelectorWindowForPendingDistributions(popupInformation)

        const newView = this.getViewForCardSelectorWindowForPendingDistributions(pendingDistributionSelectFormat, popupImageAndDescription)
        this.setNewView(newView, pendingDistributionSelectFormat.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardSelectorWindowForPendingDistributions(pendingDistributionSelectFormat)
    }

    private showCardSelectorWindowForGivingCardToAnotherPlayer(selectCardFormat: SelectCardsToGiveToAnotherPlayer) {
        const cardData = CardDataController.getCardDataForCard(selectCardFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForCardSelectorWindowForGivingCardToAnotherPlayer(popupInformation)
        popupImageAndDescription.updateBody(selectCardFormat.body)

        const newView = this.getViewForCardSelectorWindowForGivingCardToAnotherPlayer(selectCardFormat, popupImageAndDescription)
        this.setNewView(newView, selectCardFormat.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardSelectorWindowForGivingCardToAnotherPlayer(selectCardFormat)
    }

    private showCardDiscardPopup(discardCardFormat: SelectCardToDiscardFormat) {
        const newView = this.getViewForCardDiscardPopup(discardCardFormat)
        this.setNewView(newView, discardCardFormat.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardDiscardPopup(discardCardFormat)
    }

    private showSelectPlayer(selectionFormat: SelectPlayerRobberFormat) {
        const popupInformationView = this.getPopupImageAndDescriptionForSelectPlayerToRob(selectionFormat.isPirate)

        const newView = new UIGamePopupSelectPlayers(this.uiGameManager, popupInformationView, selectionFormat)
        this.setNewView(newView, selectionFormat.allowableActionState)
        this.onCanvasResize = () => this.showSelectPlayer(selectionFormat)
    }

    private showCardSelectorWindowToStealDevelopmentCard(stealDevelopmentCardFormat: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        const cardData = CardDataController.getCardDataForCard(stealDevelopmentCardFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForCardSelectorWindowToStealDevelopmentCard(popupInformation)

        const newView = this.getViewForCardSelectorWindowToStealDevelopmentCard(stealDevelopmentCardFormat, popupImageAndDescription)
        this.setNewView(newView, stealDevelopmentCardFormat.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardSelectorWindowToStealDevelopmentCard(stealDevelopmentCardFormat)
    }

    private showCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        const cardData = CardDataController.getCardDataForCard(stealResourceCardFromAnotherPlayer.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer, popupInformation)

        const newView = this.getViewForCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer, popupImageAndDescription)
        this.setNewView(newView, stealResourceCardFromAnotherPlayer.selectCardFormat.allowableActionState)
        this.onCanvasResize = () => this.showCardSelectorWindowToStealResourceCard(stealResourceCardFromAnotherPlayer)
    }

    private showSelectPlayerUsingDevelopmentCard(developmentPlayerSelectFormat: SelectPlayerUsingDevelopmentCardFormat) {
        const cardData = CardDataController.getCardDataForCard(developmentPlayerSelectFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForSelectPlayerUsingDevelopmentCard(popupInformation)

        if(developmentPlayerSelectFormat.overrideText != undefined) popupImageAndDescription.updateBody(developmentPlayerSelectFormat.overrideText)

        const newView = new UIGamePopupSelectPlayers(this.uiGameManager, popupImageAndDescription, developmentPlayerSelectFormat.selectPlayerFormat, developmentPlayerSelectFormat.developmentCardUsed)
        this.setNewView(newView, developmentPlayerSelectFormat.selectPlayerFormat.allowableActionState)
        this.onCanvasResize = () => this.showSelectPlayerUsingDevelopmentCard(developmentPlayerSelectFormat)
    }

    private showSelectPlayerAndResourceCard(selectionFormat: SelectPlayerAndResourceCardFormat) {
        const cardData = CardDataController.getCardDataForCard(selectionFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForSelectPlayerAndResourceCard(popupInformation)

        const newView = this.getViewForSelectPlayerAndResourceCard(selectionFormat, popupImageAndDescription)
        this.setNewView(newView, selectionFormat.selectPlayerFormat.allowableActionState)
        this.onCanvasResize = () => this.showSelectPlayerAndResourceCard(selectionFormat)
    }

    protected abstract createPopupSelectPlayerAndResourceCard(popupImageAndDescription: UIGamePopupInformationView, selectionFormat: SelectPlayerAndResourceCardFormat): UIGamePopupSelectPlayerAndResourceCard

    private showSelectDice(selectDiceFormat: SelectDiceFormat) {
        const cardData = CardDataController.getCardDataForCard(selectDiceFormat.developmentCardUsed)
        if(cardData == undefined) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForCardData(cardData)
        const popupImageAndDescription = this.getPopupImageAndDescriptionForSelectDice(popupInformation)

        const newView = this.getViewForSelectDice(selectDiceFormat, popupImageAndDescription)
        this.setNewView(newView, selectDiceFormat.allowableActionState)
        this.onCanvasResize = () => this.showSelectDice(selectDiceFormat)
    }

    private playSoundForCardSelectorWindow() {
        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.SelectCardsForPendingDistributions: Sound.playSound(Sound.SoundType.YourTurn); break
            default: break
        }
    }

    private isWrongState(): boolean {
        const didTurnStateChange = this.uiGameManager.gameController.currentState.turnState != this.turnStateOnPopupInitialization
        if(didTurnStateChange) return true

        const didActionStateChange = this.uiGameManager.gameController.currentState.actionState != this.allowableActionState
        if(didActionStateChange) return true

        return false
    }

    private closeUI() {
        if(this.currentView == undefined) return
        if(this.currentView.parent != undefined) this.currentView.parent.removeChild(this.currentView)
        this.currentView.destroy()
        this.currentView = undefined
        this.onCanvasResize = undefined
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.PopupActive)
    }

    private closeUIOnShowTrade() {
        if(this.currentView == undefined) return
        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    private onGameStateUpdated() {
        if(this.isWrongState()) this.closeUI()

        if(!this.uiGameManager.gameController.isMyTurn) return
        if(!this.shouldShowBeginnerPopups) return

        if(this.shouldShowRobberInfoPopup()) this.onPlayerRolledSeven()
        if(this.shouldShowInitialPlacementPopup()) this.onInitialPlacements()
        this.previousTurnState = this.uiGameManager.gameController.currentState.turnState
    }

    private getShouldShowBeginnerPopups(): boolean {
        if(this.uiGameManager.gameController.isPlayerSpectator) return false
        if(ClientUserState.userState?.hasZeroCompletedGames()) return true
        return false
    }

    private shouldShowRobberInfoPopup(): boolean {
        const isValidActionState = ActionState.PlaceRobberOrPirate == this.uiGameManager.gameController.currentState.actionState
        const isPreviousTurnStateDice = this.previousTurnState == TurnState.Dice
        this.previousTurnState = this.uiGameManager.gameController.currentState.turnState

        if(!isPreviousTurnStateDice) return false
        if(!this.uiGameManager.gameController.diceState.diceThrown) return false
        if(!isValidActionState) return false
        if(this.uiGameManager.gameController.diceState.dice1 + this.uiGameManager.gameController.diceState.dice2 != 7) return false
        return true
    }

    private onPlayerRolledSeven() {
        if(this.uiGameManager.gameController.isReplay) return

        const popupInformation = UIGamePopupInformationView.getPopupInformationForPlayerRolledSeven(this.uiGameManager)

        const newView = this.getViewForMechanicIntroductionForBeginners(popupInformation)
        this.setNewView(newView, ActionState.None)
        this.onCanvasResize = () => this.onPlayerRolledSeven()
    }

    private shouldShowInitialPlacementPopup(): boolean {
        const validGameModes = [ModeSettingEnum.Classic4P, ModeSettingEnum.Classic56P, ModeSettingEnum.Classic78P]
        if(!validGameModes.includes(this.uiGameManager.gameController.gameSettings.modeSetting)) return false
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.InitialPlacement) return false
        return true
    }

    private onInitialPlacements() {
        if(this.uiGameManager.gameController.isReplay) return

        const currentActionState = this.uiGameManager.gameController.currentState.actionState
        const validActionStates = [ActionState.InitialPlacementRoadPlacement, ActionState.InitialPlacementPlaceSettlement]
        if(!validActionStates.includes(currentActionState)) return

        const popupInformation = currentActionState == ActionState.InitialPlacementRoadPlacement ? UIGamePopupInformationView.getPopupInformationForInitialRoadPlacement(this.uiGameManager) : UIGamePopupInformationView.getPopupInformationForInitialSettlementPlacement(this.uiGameManager)

        const newView = this.getViewForMechanicIntroductionForBeginners(popupInformation)
        this.setNewView(newView, currentActionState)
        this.onCanvasResize = () => this.onInitialPlacements()
    }
}
